/**
 * Изменение цели: сначала последствия, потом согласие.
 *
 * Цели делят одни и те же деньги. «Поднять важность» никогда не значит только
 * «этой цели станет лучше» — у кого-то другого в ту же секунду убыло. Раньше
 * правка выглядела безобидной настройкой: число в карточке менялось, а то,
 * что подушка от этого закрывается на четыре месяца позже, не произносилось
 * нигде.
 *
 * Здесь разведены четыре разные вещи, которые прежде сливались в слово «план»:
 *
 * · **факт** — деньги, которые уже отложены; их эта форма не трогает;
 * · **принятый план** — версия, с которой человек согласился; она сама не
 *   меняется и прошлое не переписывает;
 * · **прогноз** — что получается из сегодняшних данных;
 * · **предлагаемое изменение** — то, что человек набирает прямо сейчас.
 *
 * Согласие — отдельное действие, и оно сохраняет **новую версию** плана, а не
 * правит старую.
 */
import { useMemo, useState } from 'react';
import {
  ZERO, clampPos, computeAvailable, formatMoney, goalEta, isAfter, monthlyForDeadline,
  type Goal, type GoalPriority, type Money, type PlainDate,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { keepMonthPlan } from './plan-version.js';
import { goalAccountId, savingAccounts } from './goal-account.js';
import {
  deleteGoal, formatAmountInput, incomeFacts, loadState, parseAmountInput, updateGoal, type AppState,
} from './store.js';
import { BigAmount, CardAccount, CardDate, CardHead, CardInput, CardMoney, CardPick, CardToggle } from './card-fields.js';

/** Важность — одни слова на всё, с пояснением из настройки. */
export const PRIORITY_OPTIONS: Array<{ id: GoalPriority; title: string; sub: string }> = [
  { id: 'critical', title: 'Критично', sub: 'сначала это' },
  { id: 'high', title: 'Высокая', sub: 'важно' },
  { id: 'medium', title: 'Средняя', sub: 'обычная цель' },
  { id: 'low', title: 'Низкая', sub: 'когда получится' },
];

const MONTHS = ['января','февраля','марта','апреля','мая','июня','июля','августа','сентября','октября','ноября','декабря'];
/** Рубли без копеек, вниз — как «Можно тратить» в шапке. */
const rub = (m: Money): string => formatMoney((Math.floor(m / 100) * 100) as Money);
const human = (d: string): string => {
  const [y, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
};

export function EditGoal({ state, id, onClose, onSaved }: {
  state: AppState; id: string; onClose: () => void; onSaved: () => void;
}) {
  const g = state.goals.find((x) => x.id === id);
  const [name, setName] = useState(g?.name ?? '');
  // Суммы показываются разрядами: «200000» в поле читается хуже, чем
  // «200 000», а ошибка на разряд в деньгах стоит дорого.
  const [target, setTarget] = useState(g ? formatAmountInput(String(g.targetAmount / 100)) : '');
  const [monthly, setMonthly] = useState(
    g ? formatAmountInput(String(g.monthlyContribution / 100)) : '');
  const [date, setDate] = useState(g?.targetDate ?? '');
  // «Без срока»: дату посчитает приложение по взносу в месяц.
  const [noDate, setNoDate] = useState(!g?.targetDate);
  const when = noDate ? '' : date;
  const [priority, setPriority] = useState<GoalPriority>(g?.priority ?? 'medium');
  const [status, setStatus] = useState<Goal['status']>(g?.status ?? 'active');
  /** С какого дня копим. Пусто — сразу. */
  const [start, setStart] = useState(g?.startDate ?? '');
  /** Где копим: у старой цели — счёт её взносов. */
  const own = savingAccounts(state.accounts);
  const heldOn = g ? goalAccountId(state, g) ?? '' : '';
  const [accountId, setAccountId] = useState(heldOn);
  const [busy, setBusy] = useState(false);

  /** Что именно человек предлагает поменять. Пустая правка — не изменение. */
  const patch = useMemo((): Partial<Goal> => {
    if (!g) return {};
    const out: Partial<Goal> = {};
    if (name.trim() && name.trim() !== g.name) out.name = name.trim();
    const t = parseAmountInput(target);
    if (t > ZERO && t !== g.targetAmount) out.targetAmount = t;
    const m = parseAmountInput(monthly);
    if (m !== g.monthlyContribution) {
      out.monthlyContribution = m;
      out.contributionMode = m > ZERO ? 'fixed' : 'residual';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(when) && when !== g.targetDate) out.targetDate = when as PlainDate;
    // «Без срока»: дата снимается — значение пустое, и при сохранении поле уходит.
    if (when === '' && g.targetDate) (out as Record<string, unknown>).targetDate = undefined;
    if (priority !== g.priority) out.priority = priority;
    if (status !== g.status) out.status = status;
    if (accountId && accountId !== (g.accountId ?? heldOn)) out.accountId = accountId;
    if (/^\d{4}-\d{2}-\d{2}$/.test(start) && start !== g.startDate) out.startDate = start as PlainDate;
    if (start === '' && g.startDate) (out as Record<string, unknown>).startDate = undefined;
    return out;
  }, [g, name, target, monthly, when, priority, status, accountId, heldOn, start]);

  const changed = Object.keys(patch).length > 0;

  /*
    Как копим: человек называет сумму в месяц или срок — второе считаем.
    Названо и то и другое, а они не сходятся, — говорим прямо и предлагаем
    поправить одно из двух.
  */
  const plan = useMemo(() => (g ? goalPlan({ ...g, ...patch }, monthly, when, state.today) : null),
    [g, patch, monthly, when, state.today]);

  // «Можно тратить» до и после: взнос в цель вычитается из него сразу.
  const spend = useMemo((): { before: Money; after: Money } | null => {
    if (!g || !changed) return null;
    const of = (goals: Goal[]) => clampPos(computeAvailable({
      ...state, goals, expectations: state.expectedIncomes, incomeFacts: incomeFacts(state.transactions),
    }).available);
    return { before: of(state.goals), after: of(state.goals.map((x) => (x.id === g.id ? { ...x, ...patch } : x))) };
  }, [g, changed, patch, state]);

  if (!g) return null;

  // Ни взноса, ни срока — посчитать нечего: это ошибка, а не «изменение».
  const missing = !plan || termsMissing(plan);

  return (
    <SheetWrap title={g.name || 'Цель'} onClose={onClose} className="rcpt"
      tip="Здесь меняются условия цели; сами деньги переводишь ты, и взнос подтверждается отдельно.">
      <div className="rcform">
        {/* Факт: деньги, которые уже отложены. Эта форма их не двигает —
            взнос подтверждается отдельным действием, на главной. */}
        <div className="rcchips"><span className="rcinfo">
          Уже отложено {formatMoney(g.currentAmount)} из {formatMoney(g.targetAmount)}</span></div>

        <BigAmount label="Сколько нужно" value={target} onChange={setTarget} />
        <div className="rccard rcsel">
          <CardInput label="Название" value={name} onChange={setName} width={170} />
          {own.length > 0 && (
            <CardAccount label="Где копим" pickerTitle="Где копим" accounts={own} value={accountId} onChange={setAccountId} />
          )}
        </div>
        {plan && <GoalTerms plan={plan} today={state.today} monthly={monthly} onMonthly={setMonthly}
          date={date} onDate={setDate} noDate={noDate} onNoDate={setNoDate}
          start={{ value: start, onChange: setStart, text: 'сразу' }} />}

        {/*
          Важность выбирает человек. Род цели её не назначает: подушка бывает
          менее срочной, чем поездка к родителям, и решать это за него
          приложение не вправе.
        */}
        <div className="rccard rcsel">
          <CardPick label="Насколько важно" value={priority} options={PRIORITY_OPTIONS}
            sub={PRIORITY_OPTIONS.find((o) => o.id === priority)?.sub ?? ''}
            tip="Важность решает, кто получит деньги первым, когда их на всех не хватает. Род цели важность не назначает: это твой выбор."
            onChange={(x) => setPriority(x as GoalPriority)} />
          <CardPick label="Состояние" value={status} onChange={(x) => setStatus(x as Goal['status'])}
            options={[
              { id: 'active', title: 'Копим', sub: 'SKOKA просит взнос с каждого прихода — по важности и сроку' },
              { id: 'paused', title: 'Пауза', sub: 'пока не копишь, взносы не нужны; собранное остаётся' },
              { id: 'achieved', title: 'Готово', sub: 'цель собрана — больше денег не просит' },
            ]} />
        </div>

        {/*
          Что изменится — до согласия, а не после, и только то, что правда
          меняется: взнос, когда соберётся и «Можно тратить».
        */}
        {changed && (
          <section className="infoblk">
            <b className="infoblk-head">Что изменится</b>
            {patch.monthlyContribution !== undefined && (
              <div className="row"><div className="rl">Откладывать в месяц</div>
                <div className="rv num">{formatMoney(g.monthlyContribution)} → {formatMoney(patch.monthlyContribution)}</div></div>
            )}
            {(patch.monthlyContribution !== undefined || patch.targetAmount !== undefined) && plan && (
              <div className="row"><div className="rl">Соберётся к</div>
                <div className="rv">{plan.eta ? human(plan.eta) : 'не соберётся — взнос не указан'}</div></div>
            )}
            {spend && spend.before !== spend.after && (
              <div className="row"><div className="rl">Можно тратить сейчас</div>
                <div className="rv num">{rub(spend.before)} → {rub(spend.after)}</div></div>
            )}
            {spend && spend.after < spend.before && (
              <div className="note">Взнос откладывается из денег, которые сейчас можно тратить, — поэтому их станет меньше.</div>
            )}
          </section>
        )}

        <button className="btn pri" disabled={busy || !changed || missing}
          onClick={async () => {
            setBusy(true);
            await updateGoal(id, patch);
            // Согласие — отдельное событие: сохраняется новая версия плана,
            // а прошлая остаётся историей решения.
            await keepMonthPlan(await loadState());
            onSaved();
          }}>
          {changed ? 'Принять изменение' : 'Изменений нет'}
        </button>
        {changed && (
          <button className="btn" disabled={busy} onClick={onClose}>Не сейчас</button>
        )}
        <button className="textact danger" disabled={busy}
          onClick={async () => {
            if (confirm('Удалить цель? Закреплённые под неё конверты тоже уйдут.')) {
              setBusy(true);
              await deleteGoal(id); onSaved();
            }
          }}>Удалить</button>
      </div>
    </SheetWrap>
  );
}

/** Что получается из взноса и срока: когда соберётся и сколько в месяц нужно к дате. */
export interface GoalPlan { m: Money; d: PlainDate | null; eta: PlainDate | null; need: Money }

export function goalPlan(draft: Goal, monthly: string, when: string, today: PlainDate): GoalPlan {
  const m = parseAmountInput(monthly);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(when) ? (when as PlainDate) : null;
  return {
    m, d,
    eta: m > ZERO ? goalEta(draft, m, today) : null,
    need: d ? monthlyForDeadline(draft, d, today) : ZERO,
  };
}

/** Ни взноса, ни срока: когда соберётся, не посчитать. */
export const termsMissing = (p: GoalPlan): boolean => p.m <= ZERO && !p.d;

/**
 * «Как копим»: взнос в месяц, «Без срока» или дата — и что из них получается.
 *
 * Один блок для новой цели и для правки — строками, как лист кредита
 * (22.10.2026). «Начать копить» — тоже здесь: прежде оно было только в
 * настройке, а цель из приложения начинала просить деньги сразу.
 */
export function GoalTerms({ plan, today, monthly, onMonthly, date, onDate, noDate, onNoDate, start }: {
  plan: GoalPlan; today: PlainDate;
  monthly: string; onMonthly: (v: string) => void;
  date: string; onDate: (v: string) => void;
  noDate: boolean; onNoDate: (v: boolean) => void;
  /** «Начать копить»: пусто — с ближайшей зарплаты (`text`, `sub`). */
  start?: { value: string; onChange: (v: string) => void; text: string; sub?: string };
}) {
  // Ещё ничего не вписано — это подсказка, а не ошибка: новая форма не ругается.
  const blank = monthly.trim() === '' && (noDate || date === '');
  const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
  // Отодвинуть начало, не сдвинув срок, значит поднять взнос. Сказать об этом
  // надо здесь же: иначе отодвинутая цель выглядит бесплатной.
  const shortened = start && isDate(start.value) && !noDate && isDate(date)
    && (Date.parse(date) - Date.parse(start.value)) / 86400000 < 30;
  return (
    <>
      <CardHead tip="Назови, сколько откладывать в месяц, или к какой дате нужна сумма — второе посчитаю.">Как копим</CardHead>
      <div className="rccard rcsel">
        <CardMoney label="Откладывать в месяц" value={monthly} onChange={onMonthly} placeholder="сколько?"
          tip="Взнос вычитается из «Можно тратить», пока не отложен в этом месяце: деньги, обещанные будущему себе, свободными не считаются." />
        <CardToggle label="Без срока" sub="дату посчитаем по взносу" checked={noDate} onChange={onNoDate} />
        {!noDate && (
          <CardDate label="К какой дате" value={date}
            min={start && isDate(start.value) && start.value > today ? start.value : today}
            text={isDate(date) ? human(date) : 'Выбрать'} onChange={onDate} />
        )}
        {start && (
          <CardDate label="Начать копить" value={start.value} min={today} clear={start.text}
            text={isDate(start.value) ? human(start.value) : start.text}
            {...(!isDate(start.value) && start.sub ? { sub: start.sub } : {})}
            tip="Начало можно отодвинуть: сначала закрыть долг, а копить с другого месяца. Цель откладывается из дохода, а не из остатка недожитого месяца."
            onChange={start.onChange} />
        )}
      </div>
      {termsMissing(plan)
        ? <div className={blank ? 'note' : 'warnline'}>Укажи сумму в месяц или дату — без них не посчитать, когда соберётся.</div>
        : noDate
          ? plan.eta && <div className="note ok">Срок посчитаем сами: по {formatMoney(plan.m)} в месяц соберётся к {human(plan.eta)}.</div>
          : <GoalPace plan={plan} onDate={(d) => { onNoDate(false); onDate(d); }}
              onMonthly={(v) => onMonthly(formatAmountInput(String(v / 100)))} />}
      {shortened && (
        <div className="warnline">
          От начала до срока меньше месяца — всю сумму придётся отложить сразу. Сдвинь срок или начало.
        </div>
      )}
    </>
  );
}

/** Сумма в месяц и срок: что получается из названного и как поправить. */
function GoalPace({ plan, onDate, onMonthly }: {
  plan: GoalPlan;
  onDate: (d: string) => void;
  onMonthly: (m: Money) => void;
}) {
  const { m, d, eta, need } = plan;
  if (m <= ZERO && !d) {
    return <div className="note">Укажи сумму в месяц или дату — второе посчитаю.</div>;
  }
  if (m > ZERO && !d) {
    return eta ? <div className="note ok">По {formatMoney(m)} в месяц соберётся к {human(eta)}.</div> : null;
  }
  if (m <= ZERO && d) {
    return (
      <div className="goalpace">
        <span>Чтобы успеть к {human(d)}, откладывай ≈ {formatMoney(need)} в месяц.</span>
        <button type="button" className="btn inline pri" onClick={() => onMonthly(need)}>Откладывать {formatMoney(need)}</button>
      </div>
    );
  }
  if (!eta || !d) return null;
  // Разница меньше месяца — это и есть «к сроку»: взнос считается помесячно.
  const late = isAfter(eta, d) && eta.slice(0, 7) > d.slice(0, 7);
  // «Раньше» — только когда откладывают больше нужного к сроку. Ровно нужный
  // взнос, округлённый до сотни, кончается в последний месяц до срока, и
  // форма говорила «соберётся раньше» про собственное предложение (28.10.2026).
  const early = m > need && isAfter(d, eta) && d.slice(0, 7) > eta.slice(0, 7);
  if (!late && !early) return <div className="note ok">По {formatMoney(m)} в месяц успеваешь к {human(d)}.</div>;
  return (
    <div className={`goalpace${late ? ' warn' : ''}`}>
      <span>{late
        ? `По ${formatMoney(m)} в месяц к ${human(d)} не успеть — соберётся только к ${human(eta)}.`
        : `По ${formatMoney(m)} в месяц соберётся раньше — к ${human(eta)}.`}</span>
      <span className="goalpace-acts">
        <button type="button" className="btn inline pri" onClick={() => onDate(eta)}>
          {late ? 'Перенести срок' : 'Поставить срок'} на {human(eta)}</button>
        {need > ZERO && need !== m && (
          <button type="button" className="btn inline" onClick={() => onMonthly(need)}>
            {late ? 'Откладывать' : 'Откладывать меньше —'} {formatMoney(need)} в месяц</button>
        )}
      </span>
    </div>
  );
}
